import React from "react";
import { MessageCircle } from "lucide-react";

/**
 * Phase B — Bouton dédié « PARTAGER MON RECORD » sur la fiche combattant.
 *
 * Comportement :
 *   1. Sur mobile où l'API Web Share est disponible (navigator.share) →
 *      on ouvre la feuille de partage native : l'utilisateur choisit
 *      WhatsApp, Messenger, SMS, Instagram, etc.
 *   2. Sur desktop (ou navigateur sans Web Share) → fallback direct sur
 *      https://wa.me/?text=… dans un nouvel onglet. Pas de menu déroulant
 *      intermédiaire — un clic, un partage.
 *
 * Template exact (validé Phase B) :
 *   PRÉNOM [NOM] « SURNOM » — R-D-L. Fiche officielle BUFL : <url>
 *
 * Le libellé « PARTAGER MON RECORD » est assumé côté client — il apparaît
 * même sur les fiches consultées par un tiers.
 */
export default function ShareRecordButton({
  firstName,
  lastName,
  nickname,
  recordStr,
  testId = "share-record-btn",
}) {
  const buildShareText = (url) => {
    const nameParts = [firstName, lastName]
      .map((s) => (s || "").trim())
      .filter(Boolean);
    const upperName = nameParts.join(" ").toUpperCase();
    const nn = (nickname || "").trim();
    // On respecte scrupuleusement le template :
    //   PRÉNOM [NOM] "SURNOM" — R-D-L. Fiche officielle BUFL : <url>
    const label = nn ? `${upperName} "${nn.toUpperCase()}"` : upperName;
    const rec = (recordStr || "0-0-0").trim();
    return `${label} — ${rec}. Fiche officielle BUFL : ${url}`;
  };

  const handleClick = async () => {
    const url =
      typeof window !== "undefined" ? window.location.href : "";
    const fullText = buildShareText(url);

    const hasWebShare =
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function";

    if (hasWebShare) {
      try {
        // On envoie l'URL séparément pour que les apps qui gèrent le rich
        // preview (WhatsApp, iMessage, Facebook) déclenchent bien l'unfurl
        // des balises OpenGraph rendues côté serveur — sans casser le
        // template si l'app choisit de concaténer texte + url.
        // Le texte inclut déjà l'URL en clair pour les apps qui l'ignorent.
        await navigator.share({
          title: `${firstName || ""} — Fiche officielle BUFL`.trim(),
          text: fullText,
          url,
        });
        return;
      } catch (_e) {
        // Utilisateur a annulé OU navigateur refuse (ex: HTTP, iframe) →
        // on retombe sur wa.me pour ne pas laisser l'action inaboutie.
      }
    }

    // Fallback desktop / navigateurs sans Web Share → WhatsApp direct
    const waHref = `https://wa.me/?text=${encodeURIComponent(fullText)}`;
    if (typeof window !== "undefined") {
      window.open(waHref, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testId}
      aria-label="Partager mon record sur WhatsApp"
      className="inline-flex items-center gap-2 h-10 md:h-11 px-3.5 md:px-5 border border-neutral-800 bg-neutral-950/80 text-neutral-200 hover:bg-burkina-green/10 hover:border-burkina-green/60 hover:text-white transition-colors duration-200 font-display uppercase tracking-widest text-[10px] md:text-[11px]"
    >
      <MessageCircle size={13} className="text-burkina-green shrink-0" />
      <span>Partager mon record</span>
    </button>
  );
}
